import uniqid from 'uniqid';
import uuidv4 from 'uuid/v4';

export class Page {
    constructor(data) {
        this.id = data.id;
        this.itemName = data.itemName;
        this.itemPath = data.itemPath;
        this.language = data.language;
        this.languages = data.languages;
        this.siteName = data.siteName;
        this.workflow = data.workflow;
        this.locked = data.locked;
        this.lockedby = data.lockedby;
        this.fields = data.fields;

        this.articleSummary = data.articleSummary;
        this.mainTitle = data.mainTitle;
        this.browserTitle = data.browserTitle;
        this.metaDescription = data.metaDescription;
        this.metaKeywords = data.metaKeywords;
        this.noIndex = data.noIndex;
        this.noFollow = data.noFollow;
        this.noArchive = data.noArchive;
        this.noSnippet = data.noSnippet;

        this.isTemplate = data.isTemplate;
        this.isPrototype = data.isPrototype;

        this.tags = data.tags;
    }
}

export class PageData {
    constructor(data) {
        this.templateId = data.templateId;
        this.path = data.path;
        this.name = data.name;
        this.templateName = data.templateName;
        this.articleSummary = data.articleSummary;
        this.mainTitle = data.mainTitle;
        this.browserTitle = data.browserTitle;
        this.metaDescription = data.metaDescription;
        this.metaKeywords = data.metaKeywords;
        this.bodyClasses = data.bodyClasses;
        this.isPrototype = data.isPrototype;
        this.noIndex = data.noIndex;
        this.noFollow = data.noFollow;
        this.noArchive = data.noArchive;
        this.noSnippet = data.noSnippet;
        this.tags = data.tags;
    }
}

export class Layout {
    constructor(data) {
        this.pageTemplateId = data.pageTemplateId;
        this.pageTemplateName = data.pageTemplateName;
        this.placeholders = data.placeholders;
    }
}

export class Option {
    constructor(data) {
        this.metadata = {
            // the name of the option
            name: data.metadata.name,

            // true if the option is is available for locked components
            lockedOption: data.metadata.lockedOption,

            // true if the option can be applied to multiple elements at once
            groupOption: data.metadata.groupOption,

            // the option will only display if the passed-in property names exist on the Flex element
            requiredProps: data.metadata.requiredProps,

            // the option will only display if the passed-in property names do NOT exist on the Flex element
            disallowedProps: data.metadata.disallowedProps,

            // an array of screen names to show the option for (e.g. "Component Editor")
            // ... if none are passed in then the option is displayed on all screens
            screens: data.metadata.screens,

            // true if the option can be applied on a per-breakpoint basis
            isResponsive: data.metadata.isResponsive,
        };

        this.component = data.component;
    }
}

export class AccordionItem {
    constructor(data) {
        this.id = data.id; // a unique identifier for the accordion item, should be kebab-case
        this.name = data.name; // the display name for the button
        this.icon = data.icon; // the icon that is displayed before the display name
        this.iconSet = data.iconSet; // the icon set of the aforementioned icon
        this.data = data.data; // any data that you wish to pass to the <slot>
    }
}

export class Preset {
    constructor(data) {
        this.id = data.id; // a unique indentifier for the preset
        this.name = data.name; // the display name
        this.classes = data.classes; // the classes that belong to this preset
        this.elementTypes = data.elementTypes; // an array of element types that this preset affects (e.g "a-heading")
    }
}

export class FlexTrigger {
    constructor(data = {}) {
        this.name = data.name; // the name of the trigger
        this.label = data.label; // the label that is displayed to users
        this.component = data.component; // the name of the component that contains the triggers options
        this.icon = data.icon; // the icon associated with this trigger
        this.iconSet = data.iconSet; // the icon set (font awesome)
    }
}

export class FlexTriggerType {
    constructor(data = {}) {
        this.name = data.name; // the name of the trigger type
        this.label = data.label; // the label that is displayed to users
    }
}

export class FlexAction {
    constructor(data = {}) {
        this.name = data.name; // the name of the action
        this.label = data.label; // the label that is displayed to users
        this.component = data.component; // the name of the component that contains the action options
    }
}

export class FlexConditionTarget {
    constructor(data = {}) {
        this.name = data.name; // the name of the condition target (e.g. "cookie", "form response", etc)
        this.label = data.label; // the label that is displayed to users
        this.component = data.component; // the name of the component that manages the target options
        this.triggers = data.triggers; // if the condition target should only display for certain triggers (see FlexTrigger), add them here
    }
}

export class FlexConditionOperator {
    constructor(data = {}) {
        this.name = data.name; // the name of the operator (e.g. "equals", "lessThan", etc)
        this.label = data.label; // the label that is displayed to users
        this.computation = data.computation; // the computational logic associated with the operator
        this.component = data.component; // the name of the component that manages the operator options
    }
}

export class FlexCondition {
    constructor(data = {}) {
        this.id = data.id || uniqid(); // a unique identifier for the condition
        this.target = data.target; // the target of this condition (instance of FlexConditionTarget)
        this.targetProperty = data.targetProperty; // the property being checked (e.g. if the target is a cookie, then this is the cookie name)
        this.targetValue = data.targetValue; // the value being checked against
        this.operator = data.operator; // the operator being used (instance of FlexConditionOperator)
    }
}

export class FlexEvent {
    constructor(data = {}) {
        this.id = data.id || uniqid(); // a unique identifier for the event
        this.label = data.label; // a (user-provided) label for the event
        this.trigger = data.trigger; // the trigger for this event (instance of FlexTrigger)
        this.action = data.action; // the action that will be triggered (instance of FlexAction)

        // the conditions that need to pass in order for the action to occur (instances of FlexCondition)
        // the array is multi-dimensional in order to allow for (future) support of both "and" and "or" conditions
        // e.g.
        // [
        //   [
        //     { conditionObj }, // AND
        //     { conditionObj }, // AND
        //   ], // OR
        //     { conditionObj }, // AND
        //     { conditionObj }, // AND
        //   ]
        // ]
        this.conditions = data.conditions || [[]];

        // event metadata for both actions AND triggers
        // n.b. this is stored on the event level (vs on the action and trigger instances) as sometimes they will need to share the same data
        this.data = data.data;
    }
}

export class FlexEventGroup {
    constructor(data = {}) {
        this.click = data.click || [];
        this.mouseenter = data.mouseenter || [];
        this.mouseleave = data.mouseleave || [];
        this.submit = data.submit || [];
        this.view = data.view || [];
        this.load = data.load || [];
        this.collectionLoad = data.collectionLoad || [];
        this.flexEvent = data.flexEvent || [];
        this.change = data.change || [];
    }
}
export class Animation {
    constructor(data = {}) {
        this.id = data.id || ''; // a unique identifier for the animation
        this.label = data.label || ''; // the label that is displayed to users
        this.tweens = data.tweens || []; // the tweens (instances of Tween) that belong to this animation
        this.repeat = data.repeat || 0; // number of times to repeat the animation. pass -1 to repeat indefinitely
        this.repeatDelay = data.repeatDelay || 0; // the amount of time (in seconds) between repeats
        this.repeatAlternate = data.repeatAlternate || false; // when set to true, each repeat alternates between going forwards and backwards
    }
}

export class Tween {
    constructor(data = {}) {
        this.id = data.id || ''; // a unique identifier for the tween
        this.duration = data.duration || 1; // how long the tween takes (in seconds)
        this.delay = data.delay || 0; // the delay before the tween starts (in seconds)
        this.ease = data.ease || ''; // defines the rate of change, see the GSAP docs for available easing options
        this.vars = data.vars || []; // the props/values (instances of TweenVar) that are being animated
    }
}

export class TweenVar {
    constructor(data = {}) {
        this.id = data.id || ''; // a unique identifier for the tween
        this.label = data.label || ''; // the label that is displayed to users
        this.prop = data.prop || ''; // the CSS property
        this.value = data.value || ''; // the CSS value
        this.category = data.category || ''; // the category it falls into (e.g. "Text" or "Color")
        this.state = data.state || 'to'; // "from" = the tweenVar represents a starting value, "to" = it represents an ending value
        this.component = data.component || null; // the Vue component that manages the UI
        this.units = data.units || ''; // the available units (e.g. "px", "%", etc)
        this.unit = data.unit || ''; // the selected unit
    }
}

export class FlexElement {
    // all base properties,
    // addtional properties needed for specific types
    constructor(data = {}) {
        this.id = data.id;
        this.idOverride = data.idOverride;
        this.name = data.name;
        this.classes = data.classes;
        this.customAttributes = data.customAttributes;
        this.htmlCustomAttributes = data.htmlCustomAttributes;
        this.useContext = data.useContext;
        this.linkedValueField = data.linkedValueField;
        this.displayName = data.displayName;
        this.editorName = data.editorName;
        this.elementType = data.elementType;
        this.globalContent = data.globalContent;
        this.isNewElement = data.isNewElement;
        this.linkedComponent = data.linkedComponent;
        this.lockComponent = data.lockComponent;
        this.pointer = data.pointer;
        this.renderingField = data.renderingField;
        this.templateId = data.templateId;
        this.templateType = data.templateType;
        this.ariaLabel = data.ariaLabel;
        this.ariaLabelledBy = data.ariaLabelledBy;
        this.ariaHidden = data.ariaHidden;
        this.accessibilityRole = data.accessibilityRole;

        // Link data
        this.anchor = data.anchor;
        this.href = data.href;
        this.isLink = data.isLink;
        this.linkType = data.linkType;
        this.mediaId = data.mediaId;
        this.queryString = data.queryString;
        this.target = data.target;
        this.title = data.title;

        // Form data
        this.inputName = data.inputName;
        this.inputType = data.inputType;
        this.pattern = data.pattern;
        this.placeholder = data.placeholder;
        this.required = data.required;
        this.validationMessage = data.validationMessage;
    }
}

// **Renderings are Organisms**
// Rendering class contains the rendering and it's
// data source to simplify it's structure.
export class Rendering extends FlexElement {
    constructor(data) {
        super(data);

        this.idOverride = data.idOverride;
        this.renderingId = data.renderingId;
        this.renderingName = data.renderingName;
        this.renderingField = data.renderingField;
        this.dataSourceId = data.dataSourceId;
        this.elements = data.elements;
        this.editorName = data.editorName;
        this.tag = data.tag;

        // data.props = "dragged in" props
        // ... these are either set in the menu JSON files, or in droppable.vue
        Object.assign(this, { ...data.props });
    }
}

export class Organism extends FlexElement {
    constructor(data) {
        super(data);

        this.idOverride = data.idOverride;
        this.elements = data.elements;
        this.tag = data.tag;
    }
}

export function getOrganismClass(elementType, pointer) {
    return {
        id: uuidv4(),
        name: `${ elementType }_${ pointer }`,
        elementType,
        pointer,
    };
}

/**
 * Atoms need to be added in 3 places,
 * 1. const atoms - here
 * 2. build class extending FlexElement below
 * 3. in processDataSources switch statement in the CMS specific data-interface
 */
export const atoms = {
    BUTTON: 'a-button',
    CHECKBOX: 'a-checkbox',
    RADIO_BUTTON: 'a-radio-button',
    HEADING: 'a-heading',
    HEADING_RICH: 'a-heading-rich',
    HTML: 'a-html',
    ICON: 'a-icon',
    IMAGE: 'a-image',
    INPUT: 'a-input',
    LINK: 'a-link',
    RICH_TEXT: 'a-rich-text',
    SELECT: 'a-select',
    TEXT_MULTI_LINE: 'a-text-multi-line',
    TEXT_SINGLE_LINE: 'a-text-single-line',
    TEXTAREA: 'a-textarea',
    VIDEO: 'a-video',
    SHOWONMAP: 'a-google-map-show-on-map',
    LABEL: 'a-label',
};

export class AButton extends FlexElement {
    constructor(data) {
        super(data);
        this.dynamicProps = 'singleLineText';
        this.singleLineText = data.singleLineText;
        this.type = data.type;
        this.requiredProperties = null;
    }
}

export class ACheckbox extends FlexElement {
    constructor(data) {
        super(data);
        this.dynamicProps = 'singleLineText';
        this.inputName = data.inputName;
        this.required = data.required;
        this.validationMessage = data.validationMessage;
        this.requiredProperties = null;
    }
}

export class ARadioButton extends FlexElement {
    constructor(data) {
        super(data);
        this.dynamicProps = 'singleLineText';
        this.inputName = data.inputName;
        this.required = data.required;
        this.validationMessage = data.validationMessage;
        this.requiredProperties = null;
    }
}

export class AHeading extends FlexElement {
    constructor(data) {
        super(data);
        this.dynamicProps = 'singleLineText';
        this.singleLineText = data.singleLineText;
        this.tag = data.tag;
        this.requiredProperties = null;
    }
}

export class AHeadingRich extends FlexElement {
    constructor(data) {
        super(data);
        this.dynamicProps = 'richText';
        this.richText = data.richText;
        this.tag = data.tag;
        this.useContext = data.useContext;
        this.requiredProperties = null;
    }
}

export class AHtml extends FlexElement {
    constructor(data) {
        super(data);
        this.dynamicProps = 'richText';
        this.richText = data.richText;
        this.requiredProperties = null;
    }
}

export class AIcon extends FlexElement {
    constructor(data) {
        super(data);
        this.dynamicProps = ['set', 'setActive', 'icon', 'iconActive'];
        this.set = data.set;
        this.setActive = data.setActive;
        this.icon = data.icon;
        this.iconActive = data.iconActive;
        this.requiredProperties = null;
    }
}

export class AImage extends FlexElement {
    constructor(data) {
        super(data);
        this.dynamicProps = ['src', 'alt'];
        this.src = data.src;
        this.alt = data.alt;
        this.width = data.width;
        this.height = data.height;
        this.mediaId = data.mediaId;
        this.isBackgroundImage = data.isBackgroundImage;
        this.chId = data.chId;
        this.requiredProperties = [{ propertyName: 'alt', displayName: 'Alt', optionName: 'image-selector' }];
    }
}

export class AInput extends FlexElement {
    constructor(data) {
        super(data);
        this.dynamicProps = ['inputValue', 'inputName', 'inputType', 'pattern', 'placeholder', 'required'];
        this.inputName = data.inputName;
        this.inputType = data.inputType;
        this.inputValue = data.inputValue;
        this.pattern = data.pattern;
        this.placeholder = data.placeholder;
        this.required = data.required;
        this.validationMessage = data.validationMessage;
        this.requiredProperties = [
            { propertyName: 'inputName', displayName: 'Input Name', optionName: 'input-name' },
        ];
    }
}

export class ALink extends FlexElement {
    constructor(data) {
        super(data);
        this.dynamicProps = ['singleLineText', 'href', 'title', 'anchor', 'queryString'];
        this.singleLineText = data.singleLineText;
        this.requiredProperties = null;
    }
}

export class ARichText extends FlexElement {
    constructor(data) {
        super(data);
        this.dynamicProps = 'richText';
        this.richText = data.richText;
        this.requiredProperties = null;
    }
}

export class ASelect extends FlexElement {
    constructor(data) {
        super(data);
        this.dynamicProps = ['inputName', 'options', 'placeholder', 'required', 'validationMessage'];
        this.inputName = data.inputName;
        this.options = data.options;
        this.placeholder = data.placeholder;
        this.required = data.required;
        this.validationMessage = data.validationMessage;
        this.requiredProperties = [{ propertyName: 'inputName', displayName: 'Input Name', optionName: 'input-name' }];
    }
}

export class ATextarea extends FlexElement {
    constructor(data) {
        super(data);
        this.dynamicProps = ['inputValue', 'inputName', 'placeholder', 'required', 'validationMessage'];
        this.inputName = data.inputName;
        this.placeholder = data.placeholder;
        this.inputValue = data.inputValue;
        this.required = data.required;
        this.validationMessage = data.validationMessage;
        this.requiredProperties = [{ propertyName: 'inputName', displayName: 'Input Name', optionName: 'input-name' }];
    }
}

export class ATextMultiLine extends FlexElement {
    constructor(data) {
        super(data);
        this.dynamicProps = 'multiLineText';
        this.multiLineText = data.multiLineText;
        this.tag = data.tag;
        this.requiredProperties = null;
    }
}

export class ATextSingleLine extends FlexElement {
    constructor(data) {
        super(data);
        this.dynamicProps = 'singleLineText';
        this.singleLineText = data.singleLineText;
        this.tag = data.tag;
        this.requiredProperties = null;
    }
}

export class ALabel extends FlexElement {
    constructor(data) {
        super(data);
        this.dynamicProps = ['singleLineText', 'labelFor'];
        this.singleLineText = data.singleLineText;
        this.labelFor = data.labelFor;
        this.requiredProperties = [{ propertyName: 'singleLineText', displayName: 'Label text', optionName: '' }, { propertyName: 'labelFor', displayName: 'Label For', optionName: 'input-label-for' }];
    }
}

export class AVideo extends FlexElement {
    constructor(data) {
        super(data);
        this.dynamicProps = 'richText';
        this.richText = data.richText;
        this.requiredProperties = null;
    }
}

export class Facet extends FlexElement {
    constructor(data) {
        super(data);
        this.dynamicProps = 'singleLineText';
        this.richText = data.richText;
        this.tag = data.tag;
        this.useContext = data.useContext;
        this.requiredProperties = null;
    }
}

export class AShowOnMap extends FlexElement {
    constructor(data) {
        super(data);
        this.dynamicProps = ['coordinates'];
        this.tag = data.tag;
        this.coordinates = data.coordinates;
        this.useContext = data.useContext;
        this.requiredProperties = null;
    }
}

export function getAtomClass(data, pointer) {
    const baseElementDefault = {
        id: uuidv4(),
        name: `${ data.name }_${ pointer }`,
        classes: '',
        elementType: data.name,
        pointer,
        templateId: '00000000-0000-0000-0000-000000000000',
        workflow: {},
    };

    switch (data.name) {
        case atoms.BUTTON: {
            return new AButton({
                ...baseElementDefault,
                singleLineText: '',
                type: '',
                ...data.props,
            });
        }

        case atoms.CHECKBOX: {
            return new ACheckbox({
                ...baseElementDefault,
                inputName: '',
                required: false,
                validationMessage: '',
                ...data.props,
            });
        }

        case atoms.RADIO_BUTTON: {
            return new ARadioButton({
                ...baseElementDefault,
                inputName: '',
                required: false,
                validationMessage: '',
                ...data.props,
            });
        }

        case atoms.HEADING: {
            return new AHeading({
                ...baseElementDefault,
                singleLineText: '',
                tag: '',
                ...data.props,
            });
        }

        case atoms.HEADING_RICH: {
            return new AHeadingRich({
                ...baseElementDefault,
                richText: '',
                tag: '',
                ...data.props,
            });
        }

        case atoms.HTML: {
            return new AHtml({
                ...baseElementDefault,
                richText: '',
                ...data.props,
            });
        }

        case atoms.ICON: {
            return new AIcon({
                ...baseElementDefault,
                set: '',
                icon: '',
                ...data.props,
            });
        }

        case atoms.IMAGE: {
            return new AImage({
                ...baseElementDefault,
                src: '',
                alt: '',
                width: '',
                height: '',
                mediaId: '00000000-0000-0000-0000-000000000000',
                chId: '',
                ...data.props,
            });
        }

        case atoms.INPUT: {
            return new AInput({
                ...baseElementDefault,
                inputName: '',
                inputType: '',
                inputValue: '',
                pattern: '',
                placeholder: '',
                required: false,
                validationMessage: '',
                ...data.props,
            });
        }

        case atoms.LINK: {
            return new ALink({
                ...baseElementDefault,
                href: '',
                anchor: '',
                linkType: 'internal',
                title: '',
                target: '',
                queryString: '',
                mediaId: '00000000-0000-0000-0000-000000000000',
                ...data.props,
            });
        }

        case atoms.RICH_TEXT: {
            return new ARichText({
                ...baseElementDefault,
                richText: '',
                ...data.props,
            });
        }

        case atoms.SELECT: {
            return new ASelect({
                ...baseElementDefault,
                inputName: '',
                options: {},
                placeholder: '',
                required: false,
                validationMessage: '',
                ...data.props,
            });
        }

        case atoms.TEXTAREA: {
            return new ATextarea({
                ...baseElementDefault,
                inputName: '',
                placeholder: '',
                required: false,
                validationMessage: '',
                ...data.props,
            });
        }

        case atoms.TEXT_MULTI_LINE: {
            return new ATextMultiLine({
                ...baseElementDefault,
                multiLineText: '',
                tag: '',
                ...data.props,
            });
        }

        case atoms.LABEL: {
            return new ALabel({
                ...baseElementDefault,
                singleLineText: '',
                tag: '',
                labelFor: '',
                ...data.props,
            });
        }

        case atoms.TEXT_SINGLE_LINE: {
            return new ATextSingleLine({
                ...baseElementDefault,
                singleLineText: '',
                tag: '',
                ...data.props,
            });
        }

        case atoms.VIDEO: {
            return new AVideo({
                ...baseElementDefault,
                value: '',
                ...data.props,
            });
        }

        case atoms.SHOWONMAP: {
            return new AShowOnMap({
                ...baseElementDefault,
                singleLineText: '',
                type: '',
                coordinates: '',
                ...data.props,
            });
        }

        default:
            console.warn(`Atom ${ data.name } not found.`);
            break;
    }
}

export class TaxonomyData {
    constructor(data) {
        this.tags = data;
    }
}
